"use client";

/**
 * Google Tag Manager Component
 *
 * Uses @next/third-parties/google for optimized GTM loading.
 * All GA4 event tracking is handled through GTM's dataLayer.
 *
 * Environment Variables:
 *   NEXT_PUBLIC_GTM_ID - Google Tag Manager container ID
 */

import { GoogleTagManager } from "@next/third-parties/google";

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;

/**
 * Analytics script component to be placed in root layout.
 *
 * Loads GTM if configured, otherwise renders nothing.
 */
export function Analytics() {
  if (!GTM_ID) {
    return null;
  }

  return <GoogleTagManager gtmId={GTM_ID} />;
}

/**
 * GTM noscript fallback for <body> tag.
 * Place this immediately after the opening <body> tag.
 *
 * Note: This is only needed when using GTM directly.
 * The @next/third-parties package handles this automatically,
 * but this is provided for manual implementations.
 */
export function GTMNoScript() {
  if (!GTM_ID) {
    return null;
  }

  return (
    <noscript>
      <iframe
        height="0"
        src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
        style={{ display: "none", visibility: "hidden" }}
        title="Google Tag Manager"
        width="0"
      />
    </noscript>
  );
}

/**
 * Check if GTM analytics is enabled (for conditional rendering/logic)
 */
export function isAnalyticsEnabled(): boolean {
  return Boolean(GTM_ID);
}
